import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { type AccountMergeRequestStore } from '@/features/admin/account-linking/AccountMergeRequestStore'
import { type AccountMergeRequestItemViewModel } from '@/features/admin/account-linking/AccountMergeRequestItemViewModel'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { AsyncPrompt } from '@/infra/AsyncPrompt'

export class IgnoreMergeRequestDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  /**
   * The account merge request to ignore.
   */
  @observable
  private accessor _accountMergeRequest: AccountMergeRequestItemViewModel | null =
    null

  constructor(
    private readonly _store: AccountMergeRequestStore,
    private readonly _refreshItems: () => Promise<void>,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * The customer's display name.
   */
  @computed
  get customerDisplayName() {
    return (
      this._accountMergeRequest?.displayName ||
      this._accountMergeRequest?.email ||
      '(Not Provided)'
    )
  }

  /**
   * Asks the user to ignore the given account merge request.
   *
   * @returns Whether the request was ignored.
   */
  async prompt(
    accountMergeRequest: AccountMergeRequestItemViewModel,
    signal?: AbortSignal,
  ) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this._accountMergeRequest = accountMergeRequest
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()

    return response ?? false
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  /**
   * Ignores the merge request.
   */
  ignore = task.resolved(async () => {
    if (!this._accountMergeRequest) {
      return
    }

    await this._store
      .ignore(this._accountMergeRequest.id)
      .catch(this._createAlertErrorHandler('Ignoring the merge request failed'))
    await this._refreshItems()
    this._prompt.respond(true)
    this.modal.close()
    this._newAlert()
      .saying('The account merge request was successfully ignored.')
      .withState('success')
      .autoDismiss()
      .show()
  })
}
